import React, { useEffect, useState, useRef } from 'react';
import {
    TextField, FormControlLabel, Box, Radio, Button, Typography, List, ListItem, Grid, Divider, Tooltip, IconButton, Paper,
    Stack
} from '@mui/material';
import { IDisclosureTrackingMapping } from '../../../models/configuration/plugin/IConfiguration';
import { ConfigurationDataType, useConfigurationData } from '../../../hooks/useConfigurationData';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DtDialog } from '../../../components/ui/dialog';

interface DisclosureTrackingMappingsDialogProps {
    open: boolean;
    closeFxtn: () => void;
    updateData: (data: IDisclosureTrackingMapping) => void;
}

const DISCLOSURE_FIELDS = [
    { key: "AffiliatedBusinessDisclosureDocumentIds", label: "Affiliated Business Disclosure" },
    { key: "CharmBookletDocumentIds", label: "CHARM Booklet" },
    { key: "SpecialInfoBookletDocumentIds", label: "Special Info Booklet" },
    { key: "HelocBrochureDocumentIds", label: "HELOC Brochure" },
    { key: "AppraisalDocumentIds", label: "Appraisal" },
    { key: "AvmDocumentIds", label: "AVM" },
    { key: "HomeCounselingDisclosureDocumentIds", label: "Home Counseling Disclosure" },
    { key: "HighCostDisclosureDocumentIds", label: "High Cost Disclosure" },
    { key: "ProviderListDocumentIds", label: "Provider List" },
    { key: "SafeHarborDocumentIds", label: "Safe Harbor" },
    { key: "LoanEstimateDocumentIds", label: "Loan Estimate" },
    { key: "ClosingDisclosureDocumentIds", label: "Closing Disclosure" },
];

const DisclosureTrackingMappingsDialog: React.FC<DisclosureTrackingMappingsDialogProps> = ({ open, closeFxtn, updateData }) => {
    const [data, setData] = useState<IDisclosureTrackingMapping | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [invalidFields, setInvalidFields] = useState<{ name: string, message: string }[]>([]);
    const { data: mappingData } = useConfigurationData(ConfigurationDataType.DISCLOSURE_TRACKING_MAPPING);
    const textFieldRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (mappingData) setData(mappingData);
    }, [mappingData]);

    useEffect(() => {
        if (open && textFieldRef.current) {
            setTimeout(() => {
                textFieldRef.current?.focus();
                textFieldRef.current?.setSelectionRange(0, textFieldRef.current.value.length);
            }, 0);
        }
    }, [open]);

    if (!mappingData || !data) return null;

    const handleChange = (field: keyof IDisclosureTrackingMapping, value: any) => {
        setData(prev => prev ? { ...prev, [field]: value } : prev);
    };

    const handleUndo = (e: React.KeyboardEvent<HTMLInputElement>, field: keyof IDisclosureTrackingMapping) => {
        if (e.ctrlKey && e.key === 'z') {
            e.preventDefault();
            setData(prev => prev ? { ...prev, [field]: mappingData[field] } : prev);
        } else {
            // Allow normal typing by stopping propagation for all other keys
            e.stopPropagation();
        }
    };

    const validateFields = () => {
        const fields = [
            { name: 'Intent to Proceed', value: data?.IntentToProceed?.DocumentIds },
            ...DISCLOSURE_FIELDS.map(f => ({ name: f.label, value: data[f.key as keyof IDisclosureTrackingMapping] })),
        ];
        const isValidSafeInteger = (val: number) => Number.isInteger(Number(val)) && Number(val) <= Number.MAX_SAFE_INTEGER && Number(val) >= Number.MIN_SAFE_INTEGER;
        const invalidFields = fields.filter(field =>
            Array.isArray(field.value) && field.value.length > 0 && !field.value.every(isValidSafeInteger)
        );
        if (invalidFields.length > 0) {
            setError(`Some option values are invalid:`);
            setInvalidFields(invalidFields.map(field => ({ name: field.name, message: 'Must be a comma separated list of integers' })));
            return false;
        }
        const invalidCommaFields = fields.filter(field =>
            Array.isArray(field.value) && field.value.length > 0 && field.value.filter(ld => !ld).length > 0
        );
        if (invalidCommaFields.length > 0) {
            setError(`Some option values are invalid:`);
            setInvalidFields(invalidCommaFields.map(field => ({ name: field.name, message: 'Enter a valid Document ID' })));
            return false;
        }
        return true;
    };

    const getFieldValue = (val: string) => val ? val.split(',').map(id => id.trim()) : [];

    const closeDialog = () => {
        if (validateFields()) {
            updateData(data as IDisclosureTrackingMapping);
            closeFxtn();
        }
    };

    return (
        <>
            <DtDialog
                title="Encompass Disclosure Tracking to Docutech Document Configuration"
                open={open}
                onClose={closeDialog}
                fullWidth
                maxWidth="lg"
                actions={<Button onClick={closeDialog} variant="contained" color="primary" size="small">Close</Button>}
            >
                <Box >
                    <Grid container spacing={3}>
                        {/* Intent to Proceed */}
                        <Grid item xs={12} md={4} lg={3}>
                            <Paper elevation={0} sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
                                <Box>
                                    <Typography fontWeight={500} mb={1} display="flex" alignItems="center">
                                        Intent to Proceed
                                        <Tooltip title="Comma separated Document IDs">
                                            <IconButton size="small" sx={{ ml: 0.5 }}>
                                                <InfoOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Typography>
                                    <TextField
                                        rows={2}
                                        multiline
                                        inputRef={textFieldRef}
                                        fullWidth
                                        value={data?.IntentToProceed?.DocumentIds?.join(',') || ''}
                                        size="small"
                                        onChange={e => handleChange("IntentToProceed", { ...data?.IntentToProceed, DocumentIds: getFieldValue(e.target.value) })}
                                        onKeyDown={e => handleUndo(e as React.KeyboardEvent<HTMLInputElement>, "IntentToProceed")}
                                        autoFocus
                                        placeholder="e.g. 123,456"
                                    />
                                </Box>
                                    <Stack direction="column">
                                        <FormControlLabel
                                            control={
                                                <Radio
                                                    onClick={() => handleChange("IntentToProceed", { ...data?.IntentToProceed, Ingestion: "FirstBorrower" })}
                                                    checked={data?.IntentToProceed?.Ingestion === "FirstBorrower"}
                                                />
                                            }
                                            label="1st Borrower"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Radio
                                                    onClick={() => handleChange("IntentToProceed", { ...data?.IntentToProceed, Ingestion: "AllRecipients" })}
                                                    checked={data?.IntentToProceed?.Ingestion === "AllRecipients"}
                                                />
                                            }
                                            label="All Recipients"
                                        />
                                    </Stack>
                            </Paper>
                        </Grid>
                        {/* Docutech Document Indices - 2 columns */}
                        <Grid item xs={12} md={8} lg={9}>
                            <Paper elevation={0} sx={{ px: 2 }}>
                                <Typography fontWeight={500} mb={1}>
                                    Docutech Document Indices (comma-delimited)
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                                <Grid container spacing={2}>
                                    {DISCLOSURE_FIELDS.map((field, idx) => (
                                        <Grid item xs={12} sm={6} md={6} lg={6} key={field.key}>
                                            <TextField
                                                label={field.label}
                                                fullWidth
                                                value={
                                                    Array.isArray(data[field.key as keyof IDisclosureTrackingMapping])
                                                        ? (data[field.key as keyof IDisclosureTrackingMapping] as number[]).join(',')
                                                        : (data[field.key as keyof IDisclosureTrackingMapping] && (data[field.key as keyof IDisclosureTrackingMapping] as any).DocumentIds
                                                            ? (data[field.key as keyof IDisclosureTrackingMapping] as any).DocumentIds.join(',')
                                                            : ''
                                                        )
                                                }
                                                size="small"
                                                onChange={e => handleChange(field.key as keyof IDisclosureTrackingMapping, getFieldValue(e.target.value))}
                                                onKeyDown={e => handleUndo(e as React.KeyboardEvent<HTMLInputElement>, field.key as keyof IDisclosureTrackingMapping)}
                                                placeholder="e.g. 123,456"
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>
            </DtDialog>
            {error && (
                <DtDialog
                    open={true}
                    onClose={() => setError(null)}
                    title="Invalid Options"
                    maxWidth="xs"
                    actions={<Button onClick={() => setError(null)}>OK</Button>}
                >
                    <Typography>{error}</Typography>
                    <List>
                        {invalidFields.map(field => (
                            <ListItem key={`${field.name}-${field.message}`}>
                                <Typography>{`• ${field.name} : ${field.message}`}</Typography>
                            </ListItem>
                        ))}
                    </List>
                </DtDialog>
            )}
        </>
    );
};

export default DisclosureTrackingMappingsDialog;